import { cn } from '@client/utils/styling/cn';
import { createLink, type LinkComponent } from '@tanstack/react-router';
import { Tooltip } from 'antd';
import { Download } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import { ButtonIcon } from './ButtonIcon';
import type { DownloadButtonProps, IconButtonLinkProps } from './types';

const sizeClasses = {
    micro: 'btn-micro',
    small: 'btn-sm',
    middle: 'btn-md',
    large: 'btn-lg',
} as const;

const iconSizes = {
    micro: 12,
    small: 14,
    middle: 16,
    large: 20,
} as const;

/**
 * Standardised download button for table row actions
 *
 * Uses accent colour by default, customisable via color prop.
 *
 * @example
 * // Button mode - for triggering downloads
 * <Button.Download onClick={() => downloadFile()} />
 *
 * @example
 * // With tooltip and loading state
 * <Button.Download
 *   onClick={handleDownload}
 *   tooltip="Download file"
 *   loading={isDownloading}
 * />
 *
 * @example
 * // With custom colour
 * <Button.Download onClick={handleDownload} color="cyan" />
 */
export function ButtonDownload({
    onClick,
    disabled,
    loading,
    tooltip = 'Download',
    size = 'middle',
    color = 'accent',
}: DownloadButtonProps) {
    return (
        <ButtonIcon
            icon={<Download />}
            color={color}
            size={size}
            tooltip={tooltip}
            disabled={disabled}
            loading={loading}
            onClick={onClick}
        />
    );
}

/**
 * Base anchor component for download links
 * Styled as an accent icon button using ButtonIcon CSS classes
 */
const ButtonDownloadLinkBase = forwardRef<HTMLAnchorElement, IconButtonLinkProps>(
    ({ color = 'accent', size = 'middle', tooltip, disabled, className, ...props }, ref) => {
        const linkClasses = useMemo(
            () =>
                cn(
                    'btn-variant',
                    'btn-icon-colored',
                    `btn-color-${color}`,
                    sizeClasses[size],
                    disabled && 'pointer-events-none opacity-50',
                    className,
                ),
            [color, size, disabled, className],
        );

        const link = (
            <a ref={ref} aria-disabled={disabled} className={linkClasses} {...props}>
                <span className="btn-icon">
                    <Download size={iconSizes[size]} />
                </span>
            </a>
        );

        if (tooltip) {
            return <Tooltip title={tooltip}>{link}</Tooltip>;
        }

        return link;
    },
);

ButtonDownloadLinkBase.displayName = 'ButtonDownloadLinkBase';

/**
 * Button.Download.Link - Type-safe router link styled as a download icon button
 *
 * Uses TanStack Router for type-safe `to`, `params`, and `search` props.
 * Accent colour by default, customisable via color prop.
 *
 * @example
 * // Navigate to a download endpoint
 * <Button.Download.Link
 *   to="/console/$orgId/files/$fileId/download"
 *   params={{ orgId: '1', fileId: '123' }}
 *   tooltip="Download file"
 * />
 */
export const ButtonDownloadLink: LinkComponent<typeof ButtonDownloadLinkBase> = createLink(ButtonDownloadLinkBase);
